import { useMemo } from 'react';

import { useTranslation } from './useTranslation';

/**
 * Provides route error messages for consistent translation
 * Used by RouteErrorElement for displaying route/page errors
 */
export function useRouteErrorMessages() {
  const { t } = useTranslation(['ui', 'common']);

  const isDevelopment = import.meta.env.DEV;

  return useMemo(
    () => ({
      // Module loading errors
      moduleLoadDev: t('ui:route_errors.module_load_dev'),
      moduleLoadProd: t('ui:route_errors.module_load_prod'),

      // HTTP errors
      notFound: t('ui:route_errors.not_found'),
      forbidden: t('ui:route_errors.forbidden'),
      serverErrorDev: (statusText: string) =>
        t('ui:route_errors.server_error_dev', { statusText }),
      serverErrorProd: t('ui:route_errors.server_error_prod'),

      // Generic errors
      genericDev: (status: number, statusText: string) =>
        t('ui:route_errors.generic_dev', { status, statusText }),
      genericProd: t('ui:route_errors.generic_prod'),

      // Unknown errors
      unknown: t('ui:route_errors.unknown'),
      unknownDetailsDev: (error: string) =>
        t('ui:route_errors.unknown_details_dev', { error }),

      // Page error titles
      pageErrorDev: t('ui:route_errors.page_error_dev'),
      pageErrorProd: t('ui:route_errors.page_error_prod'),

      // Navigation actions
      goBack: t('ui:route_errors.go_back'),
      home: t('ui:route_errors.home'),
      reloadPage: t('ui:error_boundary.reload_page'),

      // Contact support
      contactSupport: t('ui:error_boundary.contact_support'),

      // Error details (dev only)
      errorDetails: t('ui:error_boundary.error_details'),

      // Determine which messages to show based on environment
      isDevelopment,
    }),
    [t, isDevelopment]
  );
}
